import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';
import { QuestionWithOptionsService } from '@tellent-ask-hr/dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  RespondFormData,
  UnwrapSignal,
} from '@tellent-ask-hr/dynamic-forms-types';
import type { SelectTemplateItem } from '@tellent-ask-hr/templates-types';

interface RespondConfig {
  question: string;
  hint: string;
  required: boolean;
  options: Array<{ value: string }>;
}

type RespondComponentType =
  import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondSelectSingleComponent<
    RespondConfig['options'][number]['value']
  >;

export class SelectSingleConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionWithOptionsService = inject(QuestionWithOptionsService);
  private readonly i18n = inject(I18nService);

  public readonly kind: SelectTemplateItem['type'] = 'select';
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.single_choice'),
    icon: 'done',
  };

  public readonly components = {
    define: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.QuestionWithOptionsComponent),
    respond: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.RespondSelectSingleComponent),
    present: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.TextPresenterComponent),
  };

  public readonly defineForm = (data?: DefineFormData) =>
    this.questionWithOptionsService.createQuestionForm(data);

  public readonly respondConfig = (
    data: RespondConfig,
  ): UnwrapSignal<RespondComponentType['respondConfig']> => ({ ...data, kind: 'radio' });

  public readonly respondForm = (
    data: RespondFormData,
  ): UnwrapSignal<RespondComponentType['respondForm']> =>
    this.formBuilder.group({
      value: this.formBuilder.control<string | null>(
        null,
        data.required ? Validators.required : [],
      ),
    });

  public readonly presentConfig = (
    data: SelectTemplateItem,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').TextPresenterComponent['config']
  > => ({
    heading: data.name,
    value: data.value,
    kind: {
      type: 'text',
      multiline: false,
    },
  });
}
